import { getAccessToken } from './authSession';

function canRetryWithSameBody(init = {}) {
  return typeof ReadableStream === 'undefined' || !(init.body instanceof ReadableStream);
}

/**
 * fetch() wrapper that attaches the current Supabase session JWT as a
 * Bearer token on the Authorization header. Use this for every call
 * to /api/* endpoints that aren't intentionally public (sign-offer,
 * webhooks, calendar feeds).
 *
 * Returns the same Response object as fetch(). If there's no active
 * session the request still goes out (without the header) and the
 * server will respond 401 — that's the right behavior because the
 * caller's UI should redirect to /login on 401.
 */
export async function authedFetch(input, init = {}) {
  const requestHeaders = typeof Request !== 'undefined' && input instanceof Request
    ? input.headers
    : undefined;
  const headers = new Headers(init.headers || requestHeaders || {});
  const callerProvidedAuth = headers.has('Authorization');
  const token = callerProvidedAuth ? null : await getAccessToken();

  if (token && !callerProvidedAuth) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(input, { ...init, headers });
  if (response.status !== 401 || callerProvidedAuth || !canRetryWithSameBody(init)) {
    return response;
  }

  const refreshedToken = await getAccessToken({ forceRefresh: true });
  if (!refreshedToken || refreshedToken === token) return response;

  const retryHeaders = new Headers(headers);
  retryHeaders.set('Authorization', `Bearer ${refreshedToken}`);
  return fetch(input, { ...init, headers: retryHeaders });
}
